/* eslint-disable max-lines-per-function */
import './Form.scss'

import { SvgIcon } from '@bob/library'
import { AppProviderContext } from '@websrc/App/AppContext'
import { trackSubmitForm } from '@websrc/App/Helpers/tracking'
import { useLocale } from '@websrc/App/Hooks/useLocale'
import { useScript } from '@websrc/App/Hooks/useScript/useScript'
import { matomo } from '@websrc/App/Tracking/Matomo'
import { mapForm } from '@websrc/DataMappers/mapSpot'
import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { Markdown } from '../Markdown/Markdown'
import { PageMessage } from '../PageMessage/PageMessage'

declare global {
  interface Window {
    d365mktforms: any
  }
}

export const RealTimeForm: FC<
  Omit<ReturnType<typeof mapForm>, 'script' | 'realTimeFormScript'> & {
    realTimeFormScript: string
  }
> = ({
  description,
  headline,
  realTimeFormScript,
  tracking_enabled,
  action_on_submit,
  submit_page_message,
  submit_redirect_url,
  trackingId,
}) => {
  const locale = useLocale()
  const { matomoPageTemplate } = useContext(AppProviderContext)
  const [showPageMessage, setShowPageMessage] = useState(false)
  const pageMessageRef = useRef<HTMLDivElement>(null)
  const scriptLoaded = useScript(
    'https://cxppusa1formui01cdnsa01-endpoint.azureedge.net/global/FormLoader/FormLoader.bundle.js'
  )

  const findInputByLabelText = useCallback((labelText: string) => {
    const labels = document.querySelectorAll('label[title="' + labelText + '"]')
    for (var i = 0; i < labels.length; i++) {
      var label = labels[i]
      var inputId = label.getAttribute('for')
      if (inputId) {
        const inputElement = document.querySelector('input#' + inputId) as HTMLInputElement
        if (inputElement) {
          return inputElement
        }
      }
    }
    return null
  }, [])

  const populateURLBuildField = useCallback(() => {
    const params = new URLSearchParams(document.location.search)
    const URLbuildField = findInputByLabelText('URL Build ID')

    if (URLbuildField) {
      URLbuildField.value = params.get('mtm_cid') ?? ''
    }
  }, [findInputByLabelText])

  const populateReferralField = useCallback(() => {
    const referralUrlField = findInputByLabelText('Referral URL')
    if (referralUrlField) {
      referralUrlField.value = document.referrer
    }
  }, [findInputByLabelText])

  const handleSubmit = useCallback(() => {
    if (action_on_submit === 'Show message') {
      const offsetTop = pageMessageRef.current?.offsetTop
      window.scrollTo({ top: offsetTop, behavior: 'smooth' })
      setShowPageMessage(true)
    } else if (action_on_submit === 'Redirect to a new page') {
      const redirectUrl = submit_redirect_url?.startsWith('/')
        ? submit_redirect_url.slice(1)
        : `${submit_redirect_url}`
      if (redirectUrl) {
        window.location.href = `/${locale}/${redirectUrl}`
      }
    }
  }, [action_on_submit, locale, submit_redirect_url])

  const mapFormScriptData = useMemo(() => {
    const findDivElements = realTimeFormScript.match(/<div.*?>.*?<\/div>/gs)
    // if the div is not found, return null
    if (findDivElements?.length !== 1) {
      return null
    }

    const formId = findDivElements[0].match(/data-form-id\s*=\s*["'](.*?)["']/)?.[1]
    const formApiUrl = findDivElements[0].match(/data-form-api-url\s*=\s*["'](.*?)["']/)?.[1]
    const cachedFormUrl = findDivElements[0].match(/data-cached-form-url\s*=\s*["'](.*?)["']/)?.[1]
    const dataReadableEventId = findDivElements[0].match(
      /data-readable-event-id\s*=\s*["'](.*?)["']/
    )?.[1]

    // if the script is not valid, return null
    if (!formId || !formApiUrl || !cachedFormUrl) {
      return null
    }

    return {
      formId,
      formApiUrl,
      cachedFormUrl,
      dataReadableEventId,
    }
  }, [realTimeFormScript])

  // add the script to the DOM
  useEffect(() => {
    if (mapFormScriptData) {
      const afterformsubmitHandler = () => {
        handleSubmit()
        tracking_enabled && trackSubmitForm()
        matomo.trackEvent('conversion', matomoPageTemplate, ['form'], [headline], 'form')
        matomo.trackCoralEvent({
          eventAction: 'form-submit',
          eventCategory: 'sign-up',
          eventName: trackingId
            ? 'trackingId ' + trackingId
            : 'formId: ' + mapFormScriptData.formId,
        })
      }
      const afterformloadHandler = () => {
        populateURLBuildField()
        populateReferralField()

        const realTimeForm = document.querySelector('.realTimeForm')
        const form = realTimeForm?.querySelector('form')
        const inputs = form?.querySelectorAll('input')
        const submitButton = form?.querySelector('button[type="submit"]')

        if (!inputs || !form) return

        inputs?.forEach((input) => {
          input.addEventListener('blur', function () {
            if (!input.checkValidity()) {
              input.classList.add('invalid')
            }
          })

          input.addEventListener('input', function () {
            if (input.checkValidity()) {
              input.classList.remove('invalid')
            }
          })
        })

        submitButton?.addEventListener('click', function () {
          inputs.forEach((input) => {
            if (!input.checkValidity()) {
              input.classList.add('invalid')
            }
          })
        })
      }

      document.addEventListener('d365mkt-afterformsubmit', afterformsubmitHandler)
      document.addEventListener('d365mkt-afterformload', afterformloadHandler)

      return () => {
        document.removeEventListener('d365mkt-afterformsubmit', afterformsubmitHandler)
        document.removeEventListener('d365mkt-afterformload', afterformloadHandler)
      }
    }
  }, [
    mapFormScriptData,
    handleSubmit,
    tracking_enabled,
    matomoPageTemplate,
    headline,
    populateURLBuildField,
    populateReferralField,
    trackingId,
  ])

  const showSubmitMessage = showPageMessage && submit_page_message

  return (
    <div ref={pageMessageRef}>
      {showSubmitMessage ? (
        <PageMessage message={{ ...submit_page_message }} />
      ) : (
        <>
          <div className={'form'} data-t-id={trackingId}>
            <div className={'form__main-text'}>
              <h2 className={'form__main-text__headline'}>{headline}</h2>
              {description && (
                <Markdown className={'form__main-text__description'} markdown={description} />
              )}
            </div>
            <div className={'realTimeForm'}>
              {mapFormScriptData !== null ? (
                scriptLoaded === 'loaded' &&
                React.createElement(
                  window.d365mktforms.FormPlaceholder,
                  {
                    formId: mapFormScriptData?.formId,
                    formApiBaseUrl: mapFormScriptData?.formApiUrl,
                    formUrl: mapFormScriptData?.cachedFormUrl,
                    readableEventId: mapFormScriptData?.dataReadableEventId,
                  },
                  null
                )
              ) : (
                <div className={'form__error'}>
                  <SvgIcon color={'error'} name={'WARNING'} size={20} />
                  Oops! Looks like this form is missing or not set up correctly.
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
